import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PencilRuler, Sparkles } from "lucide-react";
import { api, getStudentId } from "@/lib/api";
import Navbar from "@/components/Navbar";
import VoicePromptBanner from "@/components/VoicePromptBanner";
import { useVoicePrompt } from "@/hooks/use-voice-prompt";

const BRACKET_STYLES = {
  mastered: "bg-success", practicing: "bg-primary", novice: "bg-secondary",
};

export default function Mastery() {
  const navigate = useNavigate();
  const studentId = getStudentId();
  const [student, setStudent] = useState(null);
  const [masteryList, setMasteryList] = useState([]);
  const [revision, setRevision] = useState([]);

  const { blocked: voiceBlocked, retry: retryVoice } = useVoicePrompt(
    "Here's how you're doing across all your topics.",
    "mastery-intro"
  );

  useEffect(() => {
    api.get(`/students/${studentId}`).then(({ data }) => setStudent(data));
    api.get(`/mastery/${studentId}`).then(({ data }) => setMasteryList(data));
    api.get(`/mastery/${studentId}/revision`).then(({ data }) => setRevision(data));
  }, [studentId]);

  const grouped = masteryList.reduce((acc, m) => {
    acc[m.chapter_name] = acc[m.chapter_name] || [];
    acc[m.chapter_name].push(m);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <Navbar student={student} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-2xl font-heading font-extrabold mb-8">Concept Mastery</h1>
        <VoicePromptBanner blocked={voiceBlocked} onRetry={retryVoice} testId="mastery-voice-unlock-banner" />

        {revision.length > 0 && (
          <div className="mb-10" data-testid="mastery-revision-section">
            <h2 className="text-lg font-heading font-bold mb-3 flex items-center gap-2"><Sparkles size={17} className="text-accent" /> Recommended Revision</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {revision.map((r) => (
                <button key={r.concept_id} onClick={() => navigate(`/tutor/revise/${r.concept_id}`)} data-testid={`mastery-revision-item-${r.concept_id}`}
                  className="bg-card border border-border rounded-xl p-4 text-left hover:border-accent transition-colors">
                  <p className="font-semibold text-sm">{r.concept_name}</p>
                  <p className="text-xs text-muted-foreground">{r.mastery_score}% mastery · {r.recommended_minutes} min suggested</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {Object.keys(grouped).length === 0 && (
          <p className="text-muted-foreground text-sm" data-testid="mastery-empty-state">No concepts attempted yet — head to Home and start learning!</p>
        )}

        {Object.entries(grouped).map(([chapter, concepts]) => (
          <div key={chapter} className="mb-8" data-testid={`mastery-chapter-${chapter.replace(/\s+/g, '-')}`}>
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">{chapter}</h3>
            <div className="space-y-3">
              {concepts.map((c) => (
                <div key={c.concept_id} className="bg-card border border-border rounded-xl p-4" data-testid={`mastery-concept-${c.concept_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold flex items-center gap-2"><PencilRuler size={14} className="text-muted-foreground" />{c.concept_name}</span>
                    <span className="text-xs font-bold">{c.mastery_score}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${BRACKET_STYLES[c.bracket]}`} style={{ width: `${c.mastery_score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
